import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Music, ChevronDown, Lock, Unlock, Mic, Coins, X, Loader2, AlertCircle, SlidersHorizontal } from 'lucide-react';
import { useData } from '../context/DataContext';
import { useUser } from '../context/UserContext';
import { dispatchCoinBalanceChanged } from '../lib/coinEvents';
import type { Song } from '../services/SongService';

const UNLOCK_COST = 100;

export const SongLibrary: React.FC = () => {
  const { songService, songLibraryService, virtualCurrencyService } = useData();
  const { user } = useUser();
  const navigate = useNavigate();

  const [songs, setSongs] = useState<Song[]>([]);
  const [unlockedSongIds, setUnlockedSongIds] = useState<Set<string>>(new Set());
  const [userBalance, setUserBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('All');
  const [selectedDifficulty, setSelectedDifficulty] = useState('All');
  const [confirmSong, setConfirmSong] = useState<Song | null>(null);
  const [unlocking, setUnlocking] = useState(false);
  const [unlockError, setUnlockError] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    loadData();
  }, [user]);

  const loadData = async () => {
    try {
      setLoading(true);
      const allSongs = await songService.getAllSongs();
      setSongs(allSongs);

      if (user) {
        const [library, coins] = await Promise.all([
          songLibraryService.getUserLibrary(user.id),
          virtualCurrencyService.getUserCoins(user.id),
        ]);
        setUnlockedSongIds(library);
        setUserBalance(coins?.balance || 0);
      }
    } catch (err) {
      console.error('Error loading song library:', err);
    } finally {
      setLoading(false);
    }
  };

  const isFree = (song: Song) => !!song.is_audition;
  const isUnlocked = (song: Song) => isFree(song) || unlockedSongIds.has(song.id);

  const handleSongAction = (song: Song) => {
    if (isUnlocked(song)) {
      navigate(`/record/${song.id}`);
    } else {
      setConfirmSong(song);
      setUnlockError(null);
    }
  };

  const handleUnlock = async () => {
    if (!confirmSong || !user) return;
    setUnlocking(true);
    setUnlockError(null);

    try {
      await songLibraryService.unlockSong(user.id, confirmSong.id);
      setUnlockedSongIds(prev => new Set([...prev, confirmSong.id]));
      setUserBalance(prev => prev - UNLOCK_COST);
      setConfirmSong(null);
      dispatchCoinBalanceChanged();
    } catch (err: any) {
      if (err.message === 'Insufficient tokens') {
        setUnlockError('You don\'t have enough tokens. Purchase more to unlock this song.');
      } else {
        setUnlockError('Something went wrong. Please try again.');
      }
    } finally {
      setUnlocking(false);
    }
  };

  const genres = ['All', ...new Set(songs.map(s => s.genre).filter(Boolean))];
  const difficulties = ['All', 'Easy', 'Medium', 'Hard', 'Expert'];

  const filtered = songs.filter(song => {
    const matchesSearch =
      song.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      song.artist.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesGenre = selectedGenre === 'All' || song.genre === selectedGenre;
    const matchesDifficulty = selectedDifficulty === 'All' || song.difficulty === selectedDifficulty;
    return matchesSearch && matchesGenre && matchesDifficulty;
  });

  const auditionSongs = filtered.filter(s => s.is_audition || unlockedSongIds.has(s.id));
  const catalogSongs = filtered.filter(s => !s.is_audition && !unlockedSongIds.has(s.id));

  const formatDuration = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6 flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Loader2 size={32} className="animate-spin text-kod-accent mx-auto mb-4" />
          <p className="text-white/50">Loading songs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="flex items-center justify-between gap-4 mb-5">
        <div className="flex-shrink-0">
          <h1 className="text-2xl font-bold">Song Library</h1>
          <p className="text-white/60 text-sm mt-1">Choose a song to record your performance</p>
        </div>
        <div className="flex items-center gap-2 flex-1 justify-end">
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={16} />
            <input
              type="text"
              placeholder="Search songs or artists..."
              className="w-full pl-9 pr-4 py-2 bg-white/[0.07] text-white border border-white/10 rounded-lg focus:outline-none focus:ring-1 focus:ring-kod-accent text-sm placeholder:text-white/30"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <button
            onClick={() => setFiltersOpen(o => !o)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-colors flex-shrink-0 ${
              filtersOpen || selectedGenre !== 'All' || selectedDifficulty !== 'All'
                ? 'bg-kod-accent/20 border-kod-accent/40 text-kod-accent'
                : 'bg-white/[0.07] border-white/10 text-white/60 hover:text-white hover:bg-white/10'
            }`}
          >
            <SlidersHorizontal size={15} />
            <span>Filters</span>
            {(selectedGenre !== 'All' || selectedDifficulty !== 'All') && (
              <span className="bg-kod-accent text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                {(selectedGenre !== 'All' ? 1 : 0) + (selectedDifficulty !== 'All' ? 1 : 0)}
              </span>
            )}
          </button>
        </div>
      </div>

      <div className="mb-5">
        {filtersOpen && (
          <div className="mt-2 p-3 bg-white/[0.05] border border-white/10 rounded-lg flex flex-wrap gap-3">
            <div className="flex-1 min-w-[160px]">
              <label className="block text-xs font-medium text-white/50 mb-1 uppercase tracking-wide">Genre</label>
              <div className="relative">
                <select
                  className="block w-full pl-3 pr-8 py-1.5 text-sm text-white bg-white/[0.07] border border-white/10 rounded-md focus:outline-none focus:ring-1 focus:ring-kod-accent appearance-none"
                  value={selectedGenre}
                  onChange={(e) => setSelectedGenre(e.target.value)}
                >
                  {genres.map(g => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
                <ChevronDown size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
              </div>
            </div>
            <div className="flex-1 min-w-[160px]">
              <label className="block text-xs font-medium text-white/50 mb-1 uppercase tracking-wide">Difficulty</label>
              <div className="relative">
                <select
                  className="block w-full pl-3 pr-8 py-1.5 text-sm text-white bg-white/[0.07] border border-white/10 rounded-md focus:outline-none focus:ring-1 focus:ring-kod-accent appearance-none"
                  value={selectedDifficulty}
                  onChange={(e) => setSelectedDifficulty(e.target.value)}
                >
                  {difficulties.map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
                <ChevronDown size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
              </div>
            </div>
            {(selectedGenre !== 'All' || selectedDifficulty !== 'All') && (
              <button
                onClick={() => { setSelectedGenre('All'); setSelectedDifficulty('All'); }}
                className="self-end text-xs text-white/40 hover:text-white/70 transition-colors pb-1.5"
              >
                Clear all
              </button>
            )}
          </div>
        )}
      </div>

      {auditionSongs.length > 0 && (
        <div className="mb-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {auditionSongs.map(song => (
              <SongCard
                key={song.id}
                song={song}
                unlocked={true}
                isFree={!!song.is_audition}
                onAction={() => handleSongAction(song)}
                formatDuration={formatDuration}
              />
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="flex items-center gap-2 mb-4">
          <h2 className="text-lg font-semibold">Full Catalog</h2>
          <span className="text-white/40 text-sm">({catalogSongs.length} songs)</span>
        </div>

        {catalogSongs.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {catalogSongs.map(song => (
              <SongCard
                key={song.id}
                song={song}
                unlocked={unlockedSongIds.has(song.id)}
                isFree={false}
                onAction={() => handleSongAction(song)}
                formatDuration={formatDuration}
              />
            ))}
          </div>
        ) : (
          <div className="card text-center py-12">
            <Music size={48} className="mx-auto text-white/30 mb-3" />
            <p className="text-white/50">No songs found matching your filters</p>
          </div>
        )}
      </div>

      {confirmSong && (
        <UnlockModal
          song={confirmSong}
          balance={userBalance}
          cost={UNLOCK_COST}
          unlocking={unlocking}
          error={unlockError}
          onConfirm={handleUnlock}
          onCancel={() => { setConfirmSong(null); setUnlockError(null); }}
          onBuyTokens={() => { setConfirmSong(null); navigate('/purchase-coins'); }}
        />
      )}
    </div>
  );
};

const SongCard: React.FC<{
  song: Song;
  unlocked: boolean;
  isFree: boolean;
  onAction: () => void;
  formatDuration: (s: number) => string;
}> = ({ song, unlocked, isFree, onAction, formatDuration }) => {
  return (
    <div className="card group hover:border-kod-accent/30 transition-all overflow-hidden">
      <div className="relative mb-3">
        <img
          src={song.cover_url || 'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80'}
          alt={song.title}
          className="w-full h-36 object-cover rounded-lg"
        />
        {!unlocked && (
          <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center backdrop-blur-[2px]">
            <Lock size={28} className="text-white/70" />
          </div>
        )}
        {!isFree && !unlocked && (
          <div className="absolute top-2 left-2 bg-[var(--accent)] text-white text-[10px] font-bold px-2 py-0.5 rounded flex items-center gap-1">
            <Coins size={10} />
            100 Coins
          </div>
        )}
        <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded">
          {formatDuration(song.duration)}
        </div>
      </div>

      <div className="mb-3">
        <h3 className="font-semibold text-sm truncate">{song.title}</h3>
        <p className="text-white/60 text-xs truncate">{song.artist}</p>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
            song.difficulty === 'Easy' ? 'bg-green-900/30 text-green-300' :
            song.difficulty === 'Medium' ? 'bg-blue-900/30 text-blue-300' :
            song.difficulty === 'Hard' ? 'bg-orange-900/30 text-orange-300' :
            'bg-red-900/30 text-red-300'
          }`}>
            {song.difficulty}
          </span>
          <span className="text-[10px] text-white/40">{song.genre}</span>
        </div>

        <button
          onClick={onAction}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
            unlocked
              ? 'bg-kod-accent hover:bg-kod-accent-dark text-white'
              : 'bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-300 border border-yellow-500/30'
          }`}
        >
          {unlocked ? (
            <>
              <Mic size={13} />
              Sing
            </>
          ) : (
            <>
              <Unlock size={13} />
              Unlock
            </>
          )}
        </button>
      </div>
    </div>
  );
};

const UnlockModal: React.FC<{
  song: Song;
  balance: number;
  cost: number;
  unlocking: boolean;
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
  onBuyTokens: () => void;
}> = ({ song, balance, cost, unlocking, error, onConfirm, onCancel, onBuyTokens }) => {
  const canAfford = balance >= cost;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-kod-purple-800 border border-kod-purple-600 rounded-2xl max-w-md w-full shadow-2xl overflow-hidden">
        <div className="relative h-32">
          <img
            src={song.cover_url || 'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80'}
            alt={song.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-kod-purple-800 to-transparent" />
          <button
            onClick={onCancel}
            className="absolute top-3 right-3 p-1.5 bg-black/40 hover:bg-black/60 rounded-full transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-6 -mt-6 relative">
          <h3 className="text-lg font-bold mb-1">{song.title}</h3>
          <p className="text-white/60 text-sm mb-6">{song.artist}</p>

          <div className="bg-kod-purple-700/50 rounded-xl p-4 mb-4 border border-kod-purple-600/30">
            <div className="flex items-center justify-between mb-3">
              <span className="text-white/70 text-sm">Unlock cost</span>
              <div className="flex items-center gap-1.5">
                <Coins size={16} className="text-yellow-400" />
                <span className="font-bold">{cost}</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-white/70 text-sm">Your balance</span>
              <div className="flex items-center gap-1.5">
                <Coins size={16} className="text-yellow-400" />
                <span className={`font-bold ${canAfford ? 'text-white' : 'text-red-400'}`}>
                  {Math.floor(balance).toLocaleString()}
                </span>
              </div>
            </div>
            {canAfford && (
              <div className="mt-3 pt-3 border-t border-kod-purple-600/30 flex items-center justify-between">
                <span className="text-white/70 text-sm">After unlock</span>
                <div className="flex items-center gap-1.5">
                  <Coins size={16} className="text-yellow-400" />
                  <span className="font-bold">{Math.floor(balance - cost).toLocaleString()}</span>
                </div>
              </div>
            )}
          </div>

          {error && (
            <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4">
              <AlertCircle size={16} className="text-red-400 mt-0.5 flex-shrink-0" />
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {canAfford ? (
            <button
              onClick={onConfirm}
              disabled={unlocking}
              className="w-full bg-kod-accent hover:bg-kod-accent-dark disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2"
            >
              {unlocking ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Unlocking...
                </>
              ) : (
                <>
                  <Unlock size={18} />
                  Unlock for {cost} Tokens
                </>
              )}
            </button>
          ) : (
            <div className="space-y-3">
              <p className="text-center text-sm text-white/60">
                You need <span className="text-yellow-400 font-semibold">{cost - Math.floor(balance)}</span> more tokens
              </p>
              <button
                onClick={onBuyTokens}
                className="w-full bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-300 border border-yellow-500/30 font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2"
              >
                <Coins size={18} />
                Get More Tokens
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
